import {
  Autocomplete,
  InputBaseProps,
  InputProps as StandardInputProps,
  SxProps,
  TextField,
  Theme,
} from '@mui/material';
import { InputHTMLAttributes, PropsWithChildren, ReactNode } from 'react';
import { Controller, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';
import { FieldPath } from 'react-hook-form/dist/types/path/eager';
import { sentenceCase } from 'utils/casing';
import { PropertyOfType } from 'utils/typings';

// Type that allows both strict field paths and dynamic array element paths like `array.${number}.field`
// The first part must be a key of T, ensuring type safety for the array field name
type ArrayElementPath<T extends FieldValues> = `${Extract<keyof T, string>}.${number}.${string}`;

export interface FormTextFieldProps<T extends FieldValues> {
  children?: ReactNode;
  form: UseFormReturn<T>;
  name: (FieldPath<T> & PropertyOfType<T, string | number | null>) | ArrayElementPath<T>;
  label?: string;
  disabled?: boolean;
  minLength?: number;
  maxLength?: number;
  customValidate?: RegExp;
  customValidateMessage?: string;
  helperText?: ReactNode;
  helperTextComponent?: React.ElementType;
  optional?: true;
  autoFocus?: boolean;
  multiline?: true;
  inputProps?: InputBaseProps['inputProps'];
  InputProps?: Partial<StandardInputProps>;
  sx?: SxProps<Theme>;
  helperTextSx?: SxProps<Theme>;
  type?: InputHTMLAttributes<unknown>['type'];
  variant?: 'standard' | 'filled' | 'outlined';
}

export type FormFieldPath<T extends FieldValues> =
  | (FieldPath<T> & PropertyOfType<T, string | number | null>)
  | ArrayElementPath<T>;

// eslint-disable-next-line @typescript-eslint/ban-types
export const FormTextField = <T extends {}>({
  children,
  form,
  name,
  label,
  disabled,
  minLength,
  maxLength,
  customValidate,
  customValidateMessage,
  helperText,
  helperTextComponent,
  optional,
  autoFocus,
  inputProps,
  InputProps,
  multiline,
  sx,
  helperTextSx,
  type,
  variant,
}: PropsWithChildren<FormTextFieldProps<T>>) => {
  const select = !!children;
  const label1 = label ?? sentenceCase(name);

  return (
    <Controller
      control={form.control}
      name={name as FieldPath<T>}
      rules={{
        ...(customValidate && {
          pattern: {
            value: customValidate,
            message: customValidateMessage ?? `Does not match ${customValidate.toString()}`,
          },
        }),
        validate: (value) => {
          if (optional) {
            return;
          }
          if (!(value as string)?.trim()) {
            return `${label1} is required`;
          }
          if (!value) {
            return `${label1} is required`;
          }
        },
      }}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          label={label1}
          fullWidth
          disabled={disabled}
          error={!!fieldState.error}
          helperText={fieldState.error?.message ?? helperText}
          select={select}
          sx={sx}
          FormHelperTextProps={{
            sx: helperTextSx,
            component: helperTextComponent,
          }}
          inputProps={{
            minLength,
            maxLength,
            'data-1p-ignore': '', // Do not show 1password icon for all text fields
            ...inputProps,
          }}
          InputProps={{
            ...InputProps,
          }}
          multiline={multiline}
          required={!optional}
          autoFocus={autoFocus}
          margin="normal"
          type={type ?? 'text'}
          variant={variant ?? 'outlined'}
        >
          {children}
        </TextField>
      )}
    />
  );
};

type AutocompleteProps<T extends FieldValues> = {
  optionsName: FieldPath<T> & PropertyOfType<T, Array<string>>;
  freeSolo?: boolean;
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: string) => React.ReactNode;
  getOptionLabel?: (option: string) => string;
  rules?: RegisterOptions<T>;
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const FormAutocomplete = <T extends FieldValues>(
  props: Omit<AutocompleteProps<T> & FormTextFieldProps<T>, 'multiline'>,
) => {
  const {
    optional,
    label,
    name,
    inputProps,
    InputProps,
    customValidate,
    customValidateMessage,
    minLength,
    maxLength,
    variant,
  } = props;
  const options = props.form.watch(props.optionsName) as string[];

  const labelSentenceCase = label ?? sentenceCase(name);

  return (
    <Controller
      name={props.name as FieldPath<T>}
      control={props.form.control}
      rules={
        props.rules ?? {
          ...(customValidate && {
            pattern: {
              value: customValidate,
              message: customValidateMessage ?? `Does not match ${customValidate.toString()}`,
            },
          }),
          validate: (value) => {
            if (optional) {
              return;
            }
            if (!(value as string)?.trim()) {
              return `${labelSentenceCase} is required`;
            }
            if (!value) {
              return `${labelSentenceCase} is required`;
            }
          },
        }
      }
      render={({ field, fieldState }) => (
        <Autocomplete
          freeSolo={props.freeSolo ?? true}
          value={field.value ?? ''}
          disabled={props.disabled}
          forcePopupIcon={Array.isArray(options) && options.length > 0}
          renderInput={(params) => (
            <TextField
              {...params}
              variant={variant ?? 'outlined'}
              type={props.type ?? 'text'}
              label={props.label ?? sentenceCase(props.name)}
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? props.helperText}
              margin="normal"
              required={!optional}
              slotProps={{
                htmlInput: {
                  minLength,
                  maxLength,
                  'data-1p-ignore': '', // Do not show 1password icon for all text fields
                  ...(params.inputProps ?? {}),
                  ...(inputProps ?? {}),
                },
                input: {
                  ...(params.InputProps ?? {}),
                  ...(InputProps ?? {}),
                },
              }}
              disabled={props.disabled}
              sx={props.sx}
            />
          )}
          options={options}
          getOptionLabel={props.getOptionLabel}
          multiple={false}
          onChange={(_e, value) => {
            field.onChange(value ?? '');
          }}
          onInputChange={(_e, value) => {
            // For freeSolo mode, update the field value when typing
            if (props.freeSolo ?? true) {
              field.onChange(value);
            }
          }}
          onBlur={field.onBlur}
        />
      )}
    />
  );
};
